import type { DbExecutor } from "../executor";
import type { ObjectDefinition } from "../../domain/objectDefinition";
import type { FeatureDefinition } from "../../domain/feature";

interface DefinitionRow {
  id: string;
  display_name: string;
  category: string;
  geometry_type: string;
  default_width: number;
  default_depth: number;
  default_height_in: number | null;
  default_mount_height_in: number | null;
  cost_behavior: string;
  symbol_key: string;
  wall_hosted: number;
  aggregation: string;
  quantity_rule: string;
  quantity_unit: string;
  sort_order: number;
  is_system: number;
  archived_at: string | null;
}

function mapDefinition(r: DefinitionRow): ObjectDefinition {
  return {
    id: r.id,
    displayName: r.display_name,
    category: r.category as ObjectDefinition["category"],
    geometryType: r.geometry_type as ObjectDefinition["geometryType"],
    defaultWidth: Number(r.default_width),
    defaultDepth: Number(r.default_depth),
    defaultHeightInches: r.default_height_in === null ? null : Number(r.default_height_in),
    defaultMountHeightInches: r.default_mount_height_in === null ? null : Number(r.default_mount_height_in),
    costBehavior: (r.cost_behavior === "visual" ? "visual" : "scope") as ObjectDefinition["costBehavior"],
    symbolKey: r.symbol_key,
    wallHosted: Number(r.wall_hosted) === 1,
    aggregation: r.aggregation as ObjectDefinition["aggregation"],
    quantityRule: r.quantity_rule as ObjectDefinition["quantityRule"],
    quantityUnit: r.quantity_unit as ObjectDefinition["quantityUnit"],
    sortOrder: Number(r.sort_order),
    isSystem: Number(r.is_system) === 1,
    archivedAt: r.archived_at,
  };
}

const SELECT = `SELECT id, display_name, category, geometry_type, default_width, default_depth, default_height_in,
                       default_mount_height_in, cost_behavior, symbol_key, wall_hosted, aggregation, quantity_rule,
                       quantity_unit, sort_order, is_system, archived_at
                  FROM object_definitions`;

/** Reusable graphical object library (seeded) + its suggestion joins. */
export class ObjectDefinitionRepository {
  constructor(private readonly db: DbExecutor) {}

  async listAll(): Promise<ObjectDefinition[]> {
    const rows = await this.db.select<DefinitionRow>(`${SELECT} ORDER BY sort_order, display_name`);
    return rows.map(mapDefinition);
  }

  async getById(id: string): Promise<ObjectDefinition | null> {
    const rows = await this.db.select<DefinitionRow>(`${SELECT} WHERE id = ?`, [id]);
    return rows.length === 0 ? null : mapDefinition(rows[0]);
  }

  /** Palette suggestions for a room type (never a restriction). */
  async listSuggestedForRoomType(roomTypeId: string): Promise<ObjectDefinition[]> {
    const rows = await this.db.select<DefinitionRow>(
      `SELECT d.* FROM object_definitions d
         JOIN room_type_object_suggestions s ON s.object_definition_id = d.id
        WHERE s.room_type_id = ? AND d.archived_at IS NULL
        ORDER BY s.sort_order`,
      [roomTypeId],
    );
    return rows.map(mapDefinition);
  }

  /** Feature definitions this object can create scope through (action chosen at bind time). */
  /** Reverse mapping (spec §18J spatial routing): the object definition a
   *  feature definition maps to, or null when the work is legitimately
   *  non-spatial (flooring, paint, roof, permits…). */
  async spatialObjectDefinitionFor(featureDefinitionId: string): Promise<ObjectDefinition | null> {
    const rows = await this.db.select<{ object_definition_id: string }>(
      `SELECT s.object_definition_id FROM object_definition_feature_suggestions s
         JOIN object_definitions d ON d.id = s.object_definition_id
        WHERE s.feature_definition_id = ? AND d.archived_at IS NULL
        ORDER BY s.sort_order LIMIT 1`,
      [featureDefinitionId],
    );
    return rows.length > 0 ? this.getById(rows[0].object_definition_id) : null;
  }

  async listSuggestedFeatureDefinitions(objectDefinitionId: string): Promise<FeatureDefinition[]> {
    const rows = await this.db.select<{ id: string; display_name: string; default_action: string; sort_order: number; is_system: number; archived_at: string | null }>(
      `SELECT f.id, f.display_name, f.default_action, f.sort_order, f.is_system, f.archived_at
         FROM feature_definitions f
         JOIN object_definition_feature_suggestions s ON s.feature_definition_id = f.id
        WHERE s.object_definition_id = ? AND f.archived_at IS NULL
        ORDER BY s.sort_order`,
      [objectDefinitionId],
    );
    return rows.map((r) => ({
      id: r.id,
      displayName: r.display_name,
      defaultAction: r.default_action as FeatureDefinition["defaultAction"],
      sortOrder: Number(r.sort_order),
      isSystem: Number(r.is_system) === 1,
      archivedAt: r.archived_at,
    }));
  }
}
